import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { TableLazyLoadEvent } from 'primeng/table';

// PrimeNG Modules
import { CardModule } from 'primeng/card';
import { TableModule } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { InputTextModule } from 'primeng/inputtext';
import { CalendarModule } from 'primeng/calendar';
import { MultiSelectModule } from 'primeng/multiselect';
import { DropdownModule } from 'primeng/dropdown';
import { InputGroupModule } from 'primeng/inputgroup';
import { DialogModule } from 'primeng/dialog';
import { AvatarModule } from 'primeng/avatar';
import { TagModule } from 'primeng/tag';

// App Services and Models
import { ArchiveService, ArchivedSessionSearchParams } from './archived-sessions.service';
import { ChatSession } from '../models/chat-session.model';
import { Page } from '../models/page-wrapper.model';
import { AppUser } from '../users/user.model';
import { UserService } from '../users/users.service';
import { Resource } from '../master-data/resource/resource.model';
import { ResourceService } from '../master-data/resource/resource.service';
import { HttpParams } from '@angular/common/http';
import { ScrollPanelModule } from 'primeng/scrollpanel';
import { AvatarGroupModule } from 'primeng/avatargroup';
import { DividerModule } from 'primeng/divider';
import { TooltipModule } from 'primeng/tooltip';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { IftaLabelModule } from 'primeng/iftalabel';

@Component({
  selector: 'app-archived-sessions',
  standalone: true,
  imports: [
    CommonModule, ReactiveFormsModule, CardModule, TableModule, ButtonModule, InputTextModule,
    CalendarModule, MultiSelectModule, DropdownModule, InputGroupModule, DialogModule,
    AvatarModule, TagModule, ScrollPanelModule, AvatarGroupModule, DividerModule, TooltipModule,
    InputGroupModule, InputGroupAddonModule, IftaLabelModule
  ],
  templateUrl: './archived-sessions.component.html',
  styleUrls: ['./archived-sessions.component.css']
})
export class ArchivedSessionsComponent implements OnInit {

  searchForm!: FormGroup;
  archivedSessions: ChatSession[] = [];
  totalRecords: number = 0;
  isLoading: boolean = false;

  selectedSession: ChatSession | null = null;
  isDialogVisible: boolean = false;

  // Filter options
  usersForFilter: AppUser[] = [];
  resourceTypesForFilter: Resource[] = [];
  searchTypes = [{ label: 'Lexical', value: 'LEXICAL' }, { label: 'Semantic', value: 'SEMANTIC' }];

  constructor(
    private fb: FormBuilder,
    private archiveService: ArchiveService,
    private userService: UserService,
    private resourceService: ResourceService
  ) { }

  ngOnInit(): void {
    this.buildForm();
    this.loadFilterOptions();
  }

  buildForm(): void {
    this.searchForm = this.fb.group({
      dateRange: [null],
      userIds: [[]],
      searchText: [''],
      searchType: ['LEXICAL'],
      resourceType: [null],
      businessObjectId: [null]
    });
  }

  loadFilterOptions(): void {
    this.userService.getAllUsers().subscribe(page => this.usersForFilter = page.content);
    this.resourceService.getAllResources(new HttpParams()).subscribe(page => this.resourceTypesForFilter = page.content);
  }

  loadArchivedSessions(event: TableLazyLoadEvent): void {
    this.isLoading = true;
    const formValues = this.searchForm.value;

    const params: ArchivedSessionSearchParams = {
      page: event.first !== undefined ? event.first / (event.rows || 10) : 0,
      size: event.rows || 10,
      sort: event.sortField ? `${event.sortField},${event.sortOrder === 1 ? 'asc' : 'desc'}` : '',
      lastActiveFrom: formValues.dateRange?.[0] ? new Date(formValues.dateRange[0]).toISOString() : undefined,
      lastActiveTo: formValues.dateRange?.[1] ? new Date(formValues.dateRange[1]).toISOString() : undefined,
      userIds: formValues.userIds,
      searchText: formValues.searchText,
      searchType: formValues.searchType,
      resourceType: formValues.resourceType,
      businessObjectId: formValues.businessObjectId
    };

    this.archiveService.searchArchivedSessions(params).subscribe((page: Page<ChatSession>) => {
      this.archivedSessions = page.content;
      this.totalRecords = page.totalElements;
      this.isLoading = false;
    });
  }

  onSearch(): void {
    // This is a manual trigger. We can pass a dummy event to reload the table from the first page.
    this.loadArchivedSessions({ first: 0, rows: 10 });
  }

  resetSearch(): void {
    this.searchForm.reset({
      searchType: 'LEXICAL',
      userIds: []
    });
    this.onSearch();
  }

  viewSession(session: ChatSession): void {
    this.selectedSession = session;
    this.isDialogVisible = true;
  }
}

